import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import PropertyForm, { type PropertyFormSubmit } from '../../../components/property/PropertyForm';
import { usePropertyStore, type PropertyDraft } from '../../../hooks/usePropertyStore';
import type { Property } from '../../../mocks/properties';
import { useSubscription } from '../../../hooks/useSubscription';
import { useAuth } from '../../../hooks/useAuth';
import { useCompany } from '../../../hooks/useCompany';
import { apiRequest, ApiError } from '../../../utils/apiClient';
import {
  numberFromInput,
  parseList,
  formStateToPayload,
  mapServerErrorToFields,
} from '../../../utils/propertyFormState';
import { uploadNewImages } from '../../../utils/uploadPropertyImages';

export default function CreatePropertyPage() {
  const navigate = useNavigate();
  const { addProperty } = usePropertyStore();
  const {
    isSubscribed,
    loading: subscriptionLoading,
    listingsRemaining,
    timeRemainingText,
    canCreateListing,
    incrementListingsUsed,
    subscription,
    isInherited,
    needsContactCompanyAdmin,
  } = useSubscription();
  const { user, session, loading: authLoading } = useAuth();
  const { company: callerCompany } = useCompany();
  const { t, i18n } = useTranslation(['propertyCreate', 'search', 'common']);
  const dir = i18n.dir();
  const lang = i18n.resolvedLanguage ?? 'en';
  const isRTL = dir === 'rtl';

  // FAL status check: broker/company users must have APPROVED FAL to list properties
  const isFalUser = user?.accountType === 'broker' || user?.accountType === 'company';
  const falStatus = user?.falVerificationStatus;
  const isFalBlocked = isFalUser && falStatus !== 'APPROVED';

  // Nafath identity gate. Only blocks when the user has a known Nafath status
  // and it's not VERIFIED — undefined (e.g. before Nafath is enabled on the
  // backend) is treated as "no gate" so this doesn't break existing prod
  // before NAFATH_APP_ID is set.
  const nafathStatus = user?.nafathVerificationStatus;
  const isNafathBlocked = isFalUser && nafathStatus !== undefined && nafathStatus !== 'VERIFIED';

  // Redirect to membership page if user is not subscribed. Team members
  // whose company admin hasn't subscribed see an inline "contact your
  // admin" screen instead — they can't fix this with a subscribe button.
  useEffect(() => {
    if (authLoading || subscriptionLoading) return;

    if (!user) {
      navigate('/signin?redirect=/properties/new');
      return;
    }

    if (!isSubscribed && !needsContactCompanyAdmin) {
      navigate('/membership?reason=required');
    }
  }, [user, isSubscribed, needsContactCompanyAdmin, authLoading, subscriptionLoading, navigate]);

  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, string>>({});
  // Bumped after a successful submit so PropertyForm clears its fields, exactly
  // like the original page's resetForm() call on success.
  const [resetSignal, setResetSignal] = useState(0);

  // Submit orchestration — receives the collected form + image files from
  // PropertyForm. Validation already passed in the component; here we own only
  // image upload, payload assembly, and the POST + response handling.
  const handleSubmit = async (data: PropertyFormSubmit) => {
    // Double-submit guard: bail if a submission is already in flight
    if (submitting) return;

    const { form, imageFiles, coverImageIndex, floorPlanIndices } = data;
    setSubmitting(true);
    setSubmissionError(null);
    setServerFieldErrors({});
    setUploadProgress(null);

    try {
      // Upload all NEW images via the shared helper (returns null if any fail).
      const uploaded = await uploadNewImages(imageFiles, session?.token, {
        floorPlanIndices,
        onProgress: (current, total) =>
          setUploadProgress(
            isRTL ? `جارٍ رفع الصور... (${current}/${total})` : `Uploading images... (${current}/${total})`,
          ),
      });
      if (!uploaded) {
        const msg = isRTL
          ? 'تعذّر رفع بعض الصور. يرجى إزالتها والمحاولة مرة أخرى.'
          : 'Some images failed to upload. Please remove them and try again.';
        setServerFieldErrors({ _form: msg });
        setSubmissionError(msg);
        setUploadProgress(null);
        setSubmitting(false);
        return;
      }
      const uploadedImageUrls = uploaded.uploadedUrls;
      const uploadedFloorPlanUrls = uploaded.floorPlanUrls;

      setUploadProgress(isRTL ? 'جارٍ إنشاء العقار...' : 'Creating property...');

      // Combine uploaded URLs with any manually entered URLs
      const manualGalleryUrls = parseList(form.galleryUrls).filter(url => url && !url.startsWith('data:'));
      const allImageUrls = [...uploadedImageUrls, ...manualGalleryUrls];

      // Reorder images so cover image is first (like mobile app does)
      const safeCoverIndex = Math.min(coverImageIndex, allImageUrls.length - 1);
      const reorderedUrls = allImageUrls.length > 0 && safeCoverIndex >= 0
        ? [allImageUrls[safeCoverIndex], ...allImageUrls.filter((_, i) => i !== safeCoverIndex)]
        : allImageUrls;

      // Build the API payload via the pure mapper
      const apiPayload = formStateToPayload(form, reorderedUrls, uploadedFloorPlanUrls);

      // Derive local-store values from the already-computed payload fields
      const finalMainImageUrl = apiPayload.imageUrl as string | undefined;
      const galleryUrls = (apiPayload.galleryImages as string[] | undefined) ?? [];
      const amenitiesList = apiPayload.amenities as string[];

      interface PropertyApiResponse {
        id: string;
        title: string;
        [key: string]: unknown;
      }

      const response = await apiRequest<PropertyApiResponse>('/properties', {
        method: 'POST',
        body: apiPayload,
        token: session?.token,
      });

      // Store locally for immediate display
      const localPayload: PropertyDraft = {
        id: response.id,
        title: form.title.trim(),
        description: form.description.trim(),
        price: numberFromInput(form.price),
        bedrooms: Number(numberFromInput(form.bedrooms) || 0),
        bathrooms: Number(numberFromInput(form.bathrooms) || 0),
        sqft: Math.round(numberFromInput(form.sqft) * 10.7639),
        propertyType: form.propertyType as Property['propertyType'],
        yearBuilt: Number(form.yearBuilt),
        status: form.status,
        address: form.streetAddress.trim(),
        city: form.city.trim(),
        state: form.region.trim(),
        zipCode: form.postalCode.trim(),
        latitude: form.latitude ? Number(form.latitude) : 0,
        longitude: form.longitude ? Number(form.longitude) : 0,
        parking: form.amenities.includes('parking'),
        petFriendly: form.amenities.includes('pet_friendly'),
        furnished: form.furnishingStatus === 'furnished',
        featured: false,
        imageUrl: finalMainImageUrl || '',  // Use uploaded URL
        galleryImages: galleryUrls,  // Use uploaded URLs (cover excluded)
        amenities: amenitiesList,
        utilities: [],
        agentName: form.agentName.trim(),
        agentPhone: form.agentPhone.trim(),
        agentEmail: form.agentEmail.trim(),
      };

      addProperty(localPayload);
      incrementListingsUsed();
      setCreatedId(response.id);
      setUploadProgress(null);
      setResetSignal((n) => n + 1);  // clear the form fields on success
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Failed to create property:', error);
      setUploadProgress(null);
      if (error instanceof ApiError) {
        // error.details holds the parsed backend JSON body
        const errorBody = error.details as { message?: string; details?: Array<{ path?: (string | number)[]; message: string }> } | undefined;
        const fieldErrors = mapServerErrorToFields(errorBody ?? { message: error.message });
        setServerFieldErrors(fieldErrors);
        // General/form-level message goes to the page header banner
        const generalMsg = fieldErrors._form
          ?? errorBody?.message
          ?? error.message
          ?? (isRTL ? 'فشل في إنشاء العقار' : 'Failed to create property');
        setSubmissionError(generalMsg);
      } else if (error instanceof Error) {
        setSubmissionError(error.message);
      } else {
        setSubmissionError(isRTL ? 'فشل في إنشاء العقار' : 'Failed to create property');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading while checking auth and subscription
  if (authLoading || subscriptionLoading) {
    return (
      <div className="min-h-screen bg-white" dir={dir} lang={lang}>
        <Navigation />
        <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <i className="ri-loader-4-line text-4xl text-[#004D40] animate-spin"></i>
              <p className="mt-4 text-gray-600">{t('common:common.loading')}</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Team member on a company without an active subscription — show a
  // contact-your-admin screen instead of bouncing to /membership (they
  // can't fix the company's sub themselves).
  if (!isSubscribed && needsContactCompanyAdmin) {
    return (
      <div className="min-h-screen bg-white" dir={dir} lang={lang}>
        <Navigation />
        <main className="mx-auto max-w-xl px-4 py-16 sm:py-24">
          <div className="bg-white border border-gray-200 rounded-3xl shadow-xl p-8 sm:p-10 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-100 flex items-center justify-center">
              <i className="ri-customer-service-2-line text-4xl text-blue-600" aria-hidden="true" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--pv-color-primary)] mb-3">
              {isRTL ? 'تواصل مع مدير الشركة' : 'Contact your company admin'}
            </h1>
            <p className="text-gray-600 mb-8">
              {isRTL
                ? 'شركتك ليس لديها اشتراك فعال على المنصة. تواصل مع مدير الشركة لتفعيل اشتراك الشركة قبل نشر إعلانات تحت اسم الشركة.'
                : 'Your company does not have an active subscription on the platform. Contact your company admin to activate the company subscription before you can list properties under the company.'}
            </p>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
            >
              {isRTL ? 'العودة إلى لوحة التحكم' : 'Back to dashboard'}
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!isSubscribed) {
    return null;
  }

  // A team member who only has an inherited subscription cannot list under
  // "me" — personal listings require the user's own UserSubscription. We
  // surface this by graying out the "Me" option in the picker below.
  const personalOptionLocked = isInherited;

  const formDisabled = !canCreateListing || isFalBlocked || isNafathBlocked;

  return (
    <div className="min-h-screen bg-white" dir={dir} lang={lang}>
      <Navigation />
      <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
        <header className="mb-10">
          <p className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-700">
            <i className="ri-add-circle-line mr-2"></i>
            {isRTL ? 'إضافة عقار جديد' : 'Add New Property'}
          </p>
          <h1 className="mt-4 text-3xl font-bold text-[var(--pv-color-primary)]">
            {isRTL ? 'نشر إعلان عقاري' : 'Publish Property Listing'}
          </h1>
          <p className="mt-2 text-gray-600">
            {isRTL ? 'أدخل تفاصيل العقار لنشره' : 'Enter property details to publish your listing'}
          </p>

          {/* FAL Verification Banner - Only for broker/company users with non-approved FAL */}
          {isFalBlocked && (
            <div className={`mt-6 rounded-xl border-2 p-4 ${
              falStatus === 'REJECTED'
                ? 'bg-rose-50 border-rose-300'
                : 'bg-amber-50 border-amber-300'
            }`}>
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                  falStatus === 'REJECTED' ? 'bg-rose-100' : 'bg-amber-100'
                }`}>
                  <i className={`text-xl ${
                    falStatus === 'REJECTED'
                      ? 'ri-close-circle-fill text-rose-600'
                      : 'ri-time-line text-amber-600'
                  }`}></i>
                </div>
                <div className="flex-1">
                  <h3 className={`font-semibold mb-1 ${
                    falStatus === 'REJECTED' ? 'text-rose-800' : 'text-amber-800'
                  }`}>
                    {falStatus === 'REJECTED'
                      ? (isRTL ? 'تم رفض ترخيص الفال الخاص بك' : 'Your FAL license was rejected')
                      : (isRTL ? 'ترخيص الفال قيد المراجعة' : 'FAL License Pending Verification')
                    }
                  </h3>
                  <p className={`text-sm ${
                    falStatus === 'REJECTED' ? 'text-rose-700' : 'text-amber-700'
                  }`}>
                    {falStatus === 'REJECTED'
                      ? (isRTL
                          ? 'تعذر نشر عقارات. يرجى إعادة تقديم ترخيص الفال من إعدادات حسابك.'
                          : 'You cannot list properties. Please re-submit your FAL license from your account settings.')
                      : (isRTL
                          ? 'ترخيصك قيد المراجعة من قبل الفريق. سيتم إشعارك عند الاعتماد وتصبح قادراً على نشر العقارات.'
                          : 'Your FAL license is being reviewed by our team. You will be notified once approved and can publish properties.')
                    }
                  </p>
                  <a
                    href="/settings"
                    className={`inline-flex items-center gap-1.5 mt-2 text-sm font-medium ${
                      falStatus === 'REJECTED' ? 'text-rose-700 hover:text-rose-900' : 'text-amber-700 hover:text-amber-900'
                    }`}
                  >
                    <i className="ri-settings-4-line"></i>
                    {isRTL ? 'إعدادات الحساب' : 'Account Settings'}
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Nafath Identity Banner — shown when Nafath is enabled on the
              backend and the broker hasn't verified yet. */}
          {isNafathBlocked && (
            <div className="mt-6 rounded-xl border-2 border-green-300 bg-green-50 p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <i className="ri-fingerprint-line text-xl text-green-600" aria-hidden="true" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1 text-green-800">
                    {isRTL ? 'يلزم التحقق من الهوية عبر نفاذ' : 'Nafath identity verification required'}
                  </h3>
                  <p className="text-sm text-green-700 mb-2">
                    {isRTL
                      ? 'تحقق من هويتك عبر تطبيق نفاذ قبل نشر العقارات. لن يستغرق الأمر سوى دقيقة.'
                      : 'Verify your identity via the Nafath app before listing properties. Takes about a minute.'}
                  </p>
                  <Link
                    to="/account/verify-identity"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700 hover:text-green-900"
                  >
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                    {isRTL ? 'بدء التحقق' : 'Start verification'}
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Subscription Info Banner */}
          {subscription && (
            <div className="mt-6 rounded-xl bg-[var(--pv-color-surface-sunken)] border border-[var(--pv-border-soft)] p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <i className="ri-stack-line text-xl text-[#004D40]"></i>
                    <div>
                      <p className="text-sm text-gray-600">{isRTL ? 'العقارات المتبقية' : 'Listings Remaining'}</p>
                      <p className="text-lg font-bold text-[#003930]">{listingsRemaining} / {subscription.maxListings}</p>
                    </div>
                  </div>
                  <div className="h-10 w-px bg-[#DDEEEB]" />
                  <div className="flex items-center gap-2">
                    <i className="ri-time-line text-xl text-blue-600"></i>
                    <div>
                      <p className="text-sm text-gray-600">{isRTL ? 'الوقت المتبقي' : 'Time Remaining'}</p>
                      <p className="text-lg font-bold text-blue-700">{timeRemainingText}</p>
                    </div>
                  </div>
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  canCreateListing ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {canCreateListing ? (isRTL ? '✓ يمكنك الإعلان' : '✓ Can List') : (isRTL ? '✗ الحد الأقصى' : '✗ Limit Reached')}
                </span>
              </div>
            </div>
          )}

          {createdId && (
            <div className="mt-6 rounded-lg border border-green-200 bg-green-50 p-4 text-green-900">
              <div className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-xl text-green-600"></i>
                <span>{isRTL ? 'تم نشر العقار بنجاح!' : 'Property published successfully!'}</span>
              </div>
              <Button size="sm" className="mt-3" onClick={() => navigate(`/property/${createdId}`)}>
                {isRTL ? 'عرض العقار' : 'View Property'}
              </Button>
            </div>
          )}

          {submissionError && (
            <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-900">
              <div className="flex items-center gap-2">
                <i className="ri-error-warning-fill text-xl text-red-600"></i>
                <span>{submissionError}</span>
              </div>
            </div>
          )}
        </header>

        <PropertyForm
          variant="broker"
          showListUnder={!!callerCompany}
          companyName={callerCompany
            ? (isRTL
                ? (callerCompany.nameAr || callerCompany.name)
                : (callerCompany.name || callerCompany.nameAr))
            : null}
          personalOptionLocked={personalOptionLocked}
          submitting={submitting}
          serverErrors={Object.keys(serverFieldErrors).length ? serverFieldErrors : undefined}
          submitLabel={isRTL ? 'نشر العقار' : 'Publish Listing'}
          onSubmit={handleSubmit}
          formDisabled={formDisabled}
          uploadProgress={uploadProgress}
          resetSignal={resetSignal}
          onReset={() => {
            setCreatedId(null);
            setSubmissionError(null);
            setUploadProgress(null);
          }}
        />
      </main>
      <Footer />
    </div>
  );
}
